import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Button, Input, Text, useToast, Progress, Badge, 
  useColorModeValue, Stack, FormControl, FormLabel,
  Alert, AlertIcon, Table, Thead, Tbody, Tr, Th, Td,
  Heading, Container, Flex, HStack, VStack, Divider,
  Select, Grid, GridItem
} from "@chakra-ui/react";
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import {
  createCustomer,
  updateCustomer,
  getCustomerByUsername
} from "services/woo";
import { signUpApoderado } from "services/userService";
import { useModuleCheck, MODULES } from "utils/site";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";

const getMeta = (customer, key) => {
  const m = customer && Array.isArray(customer.meta_data)
    ? customer.meta_data.find(x => x.key === key)
    : null;
  return m && m.value != null ? String(m.value).trim() : '';
};

// Calcula qué quedará realmente en el usuario (misma lógica que usa processUser)
// existing: undefined = aún no consultado, null = no existe, objeto = cliente actual
const buildPlan = (user, existing) => {
  const groupInFile = getMeta({ meta_data: user.meta_data }, 'group_name');
  if (existing === undefined) {
    return { pending: true, finalGroup: groupInFile };
  }
  const currentEmail = existing ? (existing.email || '') : '';
  const currentGroup = existing ? getMeta(existing, 'group_name') : '';

  let finalEmail, emailNote;
  if (!existing) {
    finalEmail = user.email;
    emailNote = user._emailFromFile ? 'Nuevo' : 'Nuevo (generado por RUT)';
  } else if (!user._emailFromFile) {
    finalEmail = currentEmail;
    emailNote = 'Se conserva (el archivo no trae email)';
  } else if (currentEmail.toLowerCase() === user.email.toLowerCase()) {
    finalEmail = currentEmail;
    emailNote = 'Sin cambios';
  } else {
    finalEmail = user.email;
    emailNote = 'Cambia';
  }

  let finalGroup, groupNote;
  if (!groupInFile) {
    finalGroup = currentGroup;
    groupNote = existing ? 'Se conserva (archivo sin grado/tipo)' : 'Sin curso en archivo';
  } else if (!existing) {
    finalGroup = groupInFile;
    groupNote = 'Nuevo';
  } else if (currentGroup === groupInFile) {
    finalGroup = groupInFile;
    groupNote = 'Sin cambios';
  } else {
    finalGroup = groupInFile;
    groupNote = 'Cambia';
  }

  return {
    pending: false,
    exists: !!existing,
    currentEmail, finalEmail, emailNote,
    currentGroup, finalGroup, groupNote
  };
};

const UnifiedUserImporter = () => {
  // Estados principales
  const [operationName, setOperationName] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileData, setFileData] = useState([]);
  const [isExcel, setIsExcel] = useState(true);
  const [showMappingUI, setShowMappingUI] = useState(false);
  const [processedUsers, setProcessedUsers] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [existingMap, setExistingMap] = useState({});
  const [isLookingUp, setIsLookingUp] = useState(false);
  
  // Estado de módulos - memoizado
  const moduleStatus = useMemo(() => ({
    [MODULES.SURVEYS]: useModuleCheck(MODULES.SURVEYS)
  }), []);
  
  // Mapeo de columnas
  const [columnMap, setColumnMap] = useState({
    // Alumno
    studentRun: '',
    studentName: '',
    studentLastNameP: '',
    studentLastNameM: '',
    studentEmail: '',
    studentAddress: '',
    studentCommune: '',
    educationType: '',
    gradeName: '',
    courseName: '',
    enrollment: '',
    studentPhone: '',
    emergencyPhone: '',
    
    // Apoderado Financiero
    finRun: '',
    finName: '',
    finLastNameP: '',
    finLastNameM: '',
    finEmail: '',
    finPhone: '',
    finAddress: '',
    finCommune: '',
    
    // Apoderado Académico
    acadRun: '',
    acadName: '',
    acadLastNameP: '',
    acadLastNameM: '',
    acadPhone: '',
    acadAddress: '',
    acadCommune: ''
  });
  
  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    errors: 0,
    created: 0,
    updated: 0,
    laravelRegistered: 0
  });

  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Auto mapeo de columnas
  const autoMapColumns = useCallback((headers) => {
    const mappings = {
      studentRun: ['run', 'rut', 'documento'],
      studentName: ['nombre'],
      studentLastNameP: ['apellido paterno', 'apellidopaterno'],
      studentLastNameM: ['apellido materno', 'apellidomaterno'],
      studentEmail: ['email', 'correo'],
      studentAddress: ['direccion', 'dirección', 'address'],
      studentCommune: ['comuna', 'ciudad', 'city'],
      educationType: ['tipo enseñanza', 'tipoenseñanza'],
      gradeName: ['nombre grado', 'grado'],
      courseName: ['nombre curso', 'curso'],
      enrollment: ['nro. matricula', 'matricula'],
      studentPhone: ['teléfono', 'telefono'],
      emergencyPhone: ['teléfono caso de emergencia'],
      
      finRun: ['run financiero', 'rut financiero'],
      finName: ['nombre financiero'],
      finLastNameP: ['apellido paterno financiero'],
      finLastNameM: ['apellido materno financiero'],
      finEmail: ['email financiero'],
      finPhone: ['teléfono celular financiero'],
      finAddress: ['direccion financiero', 'dirección financiero', 'direccion apoderado', 'dirección apoderado'],
      finCommune: ['comuna financiero', 'ciudad financiero', 'comuna apoderado', 'ciudad apoderado'],
      
      acadRun: ['run académico', 'rut academico'],
      acadName: ['nombre académico'],
      acadLastNameP: ['apellido paterno académico'],
      acadLastNameM: ['apellido materno académico'],
      acadPhone: ['teléfono celular académico'],
      acadAddress: ['direccion académico', 'dirección académico'],
      acadCommune: ['comuna académico', 'ciudad académico']
    };

    const newMap = {};
    Object.keys(mappings).forEach(key => {
      const found = headers.find(header => {
        const h = header.toLowerCase().trim();
        return mappings[key].some(pattern => h.includes(pattern.toLowerCase()));
      });
      newMap[key] = found || '';
    });
    
    setColumnMap(newMap);
  }, []);

  // Procesar RUN
  const formatRun = useCallback((run) => {
    if (!run) return '';
    let formatted = String(run).replace(/\./g, '').trim();
    if (formatted && !formatted.includes('-')) {
      const dv = formatted.slice(-1);
      const number = formatted.slice(0, -1);
      formatted = `${number}-${dv}`;
    }
    return formatted;
  }, []);

  // Obtener valor de celda
  const getCellValue = useCallback((row, column) => {
    if (!column) return '';
    
    if (isExcel) {
      const index = fileHeaders.indexOf(column);
      return index !== -1 && row[index] ? String(row[index]).trim() : '';
    } else {
      return row[column] ? String(row[column]).trim() : '';
    }
  }, [fileHeaders, isExcel]);

  // Procesar datos del apoderado
  const getApoderadoData = useCallback((row) => {
    // Intentar financiero primero
    let run = getCellValue(row, columnMap.finRun);
    let name = getCellValue(row, columnMap.finName);
    let lastNameP = getCellValue(row, columnMap.finLastNameP);
    let lastNameM = getCellValue(row, columnMap.finLastNameM);
    let email = getCellValue(row, columnMap.finEmail);
    let phone = getCellValue(row, columnMap.finPhone);
    let address = getCellValue(row, columnMap.finAddress);
    let commune = getCellValue(row, columnMap.finCommune);
    
    // Si no hay financiero, usar académico
    if (!run && !name) {
      run = getCellValue(row, columnMap.acadRun);
      name = getCellValue(row, columnMap.acadName);
      lastNameP = getCellValue(row, columnMap.acadLastNameP);
      lastNameM = getCellValue(row, columnMap.acadLastNameM);
      phone = getCellValue(row, columnMap.acadPhone);
      address = getCellValue(row, columnMap.acadAddress);
      commune = getCellValue(row, columnMap.acadCommune);
      // email se mantiene vacío para académico
    }
    // Fallback individual: si falta dirección/comuna del financiero, usar académico
    if (!address) address = getCellValue(row, columnMap.acadAddress);
    if (!commune) commune = getCellValue(row, columnMap.acadCommune);
    
    // Formatear RUN
    if (run) run = formatRun(run);
    
    // Construir nombre completo
    let fullName = name;
    if (lastNameP || lastNameM) {
      const lastName = [lastNameP, lastNameM].filter(Boolean).join(' ');
      fullName = name ? `${name} ${lastName}` : lastName;
    }
    
    return { run, fullName, email, phone, address, commune };
  }, [columnMap, getCellValue, formatRun]);

  // Generar usuarios procesados
  const generateProcessedUsers = useCallback(() => {
    if (!fileData.length) return [];
    
    const processed = fileData.map((row, index) => {
      const studentRun = formatRun(getCellValue(row, columnMap.studentRun));
      if (!studentRun) return null;
      
      const studentName = getCellValue(row, columnMap.studentName);
      if (!studentName) return null;
      
      const lastNameP = getCellValue(row, columnMap.studentLastNameP);
      const lastNameM = getCellValue(row, columnMap.studentLastNameM);
      
      let lastName = '';
      if (lastNameP || lastNameM) {
        lastName = [lastNameP, lastNameM].filter(Boolean).join(' ');
      } else {
        // Extraer del nombre si es necesario
        const nameParts = studentName.split(' ');
        if (nameParts.length > 2) {
          lastName = nameParts.slice(-2).join(' ');
        } else {
          lastName = 'Sin Apellido';
        }
      }
      
      const address = getCellValue(row, columnMap.studentAddress);
      const commune = getCellValue(row, columnMap.studentCommune);
      const educationType = getCellValue(row, columnMap.educationType);
      const gradeName = getCellValue(row, columnMap.gradeName);
      const courseName = getCellValue(row, columnMap.courseName);
      const enrollment = getCellValue(row, columnMap.enrollment);
      const studentPhone = getCellValue(row, columnMap.studentPhone);
      const emergencyPhone = getCellValue(row, columnMap.emergencyPhone);

      // Datos del apoderado (se calcula antes del email para usar finEmail como fuente primaria)
      const apoderado = getApoderadoData(row);

      // Email principal = apoderado financiero. Fallback: columna email del alumno, luego email generado por RUT.
      const fileEmail = apoderado.email || getCellValue(row, columnMap.studentEmail);
      const email = fileEmail || `${studentRun}@blaspascal.cl`;

      // Suscripción
      const subscription = educationType.toLowerCase().includes('parvularia')
        ? 'Mensualidad PreBásica'
        : 'Mensualidad BásicaMedia';

      // Grupo
      let group = '';
      if (gradeName && educationType) {
        group = `${educationType.trim()} - ${gradeName.trim()}`;
        if (courseName) group += ` - ${courseName.trim()}`;
      }

      // Meta data
      const metaData = [
        { key: 'suscription_name', value: subscription }
      ];
      // No enviar group_name vacío: pisaría el curso actual del alumno
      if (group) metaData.push({ key: 'group_name', value: group });

      if (apoderado.run) metaData.push({ key: 'apoderado_rut', value: apoderado.run });
      if (apoderado.fullName) metaData.push({ key: 'apoderado_nombre', value: apoderado.fullName });
      // apoderado_email siempre sincronizado con customer.email (el correo de contacto financiero)
      metaData.push({ key: 'apoderado_email', value: email });
      if (apoderado.phone) metaData.push({ key: 'apoderado_telefono', value: apoderado.phone });
      if (enrollment) metaData.push({ key: 'numero_matricula', value: enrollment });
      if (studentPhone) metaData.push({ key: 'telefono', value: studentPhone });
      if (emergencyPhone) metaData.push({ key: 'telefono_emergencia', value: emergencyPhone });
      if (address) metaData.push({ key: 'direccion', value: address });
      if (commune) metaData.push({ key: 'comuna', value: commune });
      if (apoderado.address) metaData.push({ key: 'apoderado_direccion', value: apoderado.address });
      if (apoderado.commune) metaData.push({ key: 'apoderado_comuna', value: apoderado.commune });

      const billingAddress = apoderado.address || address;
      const billingCity = apoderado.commune || commune;
      return {
        username: studentRun,
        first_name: studentName,
        last_name: lastName,
        email: email,
        billing: {
          email: email,
          address_1: billingAddress || undefined,
          city: billingCity || undefined,
        },
        meta_data: metaData,
        // Para vista previa
        _emailFromFile: !!fileEmail,
        _educationType: educationType,
        _gradeName: gradeName,
        _courseName: courseName,
        _apoderadoRut: apoderado.run,
        _apoderadoNombre: apoderado.fullName,
        _apoderadoEmail: apoderado.email,
        _apoderadoTelefono: apoderado.phone,
        _apoderadoDireccion: apoderado.address,
        _apoderadoComuna: apoderado.commune,
        _enrollment: enrollment,
        _address: address,
        _commune: commune
      };
    }).filter(Boolean);
    
    return processed;
  }, [fileData, columnMap, getCellValue, formatRun, getApoderadoData]);

  // Efecto para regenerar usuarios cuando cambia el mapeo
  useEffect(() => {
    if (showMappingUI && fileData.length > 0) {
      const users = generateProcessedUsers();
      setProcessedUsers(users);
    }
  }, [columnMap, showMappingUI, generateProcessedUsers]);

  // Consultar usuarios existentes de las filas visibles de la vista previa
  useEffect(() => {
    const visible = processedUsers.slice(0, 10);
    if (!visible.length) {
      setExistingMap({});
      return;
    }
    let cancelled = false;
    setIsLookingUp(true);
    setExistingMap({});
    Promise.all(visible.map(async (u) => {
      try {
        const c = await getCustomerByUsername(u.username);
        return [u.username, c || null];
      } catch (e) {
        return [u.username, undefined];
      }
    })).then(entries => {
      if (cancelled) return;
      setExistingMap(Object.fromEntries(entries));
      setIsLookingUp(false);
    });
    return () => { cancelled = true; };
  }, [processedUsers]);

  // Manejar selección de archivo
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const extension = file.name.split('.').pop().toLowerCase();
    if (!['xls', 'xlsx', 'csv'].includes(extension)) {
      toast({
        title: "Formato inválido",
        description: "Solo se aceptan archivos Excel o CSV",
        status: "error",
        duration: 3000
      });
      return;
    }

    setFileName(file.name);
    setProcessedUsers([]);
    setResults([]);
    setShowMappingUI(false);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (['xls', 'xlsx'].includes(extension)) {
          const workbook = XLSX.read(e.target.result, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          
          if (data.length <= 1) throw new Error('Archivo vacío');
          
          setFileHeaders(data[0]);
          setFileData(data.slice(1));
          setIsExcel(true);
          autoMapColumns(data[0]);
        } else {
          Papa.parse(e.target.result, {
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
              const headers = Object.keys(result.data[0] || {});
              setFileHeaders(headers);
              setFileData(result.data);
              setIsExcel(false);
              autoMapColumns(headers);
            }
          });
        }
        
        setShowMappingUI(true);
        
        toast({
          title: "Archivo cargado",
          description: "Revisa y ajusta el mapeo de columnas",
          status: "success",
          duration: 3000
        });
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 3000
        });
      }
    };

    if (['xls', 'xlsx'].includes(extension)) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }

    event.target.value = '';
  }, [autoMapColumns, toast]);

// Actualizar mapeo
  const updateMapping = (field, value) => {
    setColumnMap(prev => ({ ...prev, [field]: value }));
  };

  // Procesar usuario individual - VERSIÓN DUAL AUTOMÁTICA
  const processUser = async (user) => {
    try {
      const existing = (await getCustomerByUsername(user.username)) || null;
      const plan = buildPlan(user, existing);

      let registrationResult = { success: true }; // Default value
      
      // 1. REGISTRO EN LARAVEL (automático si módulo activo)
      if (moduleStatus[MODULES.SURVEYS]) {
        registrationResult = await signUpApoderado({
          username: user.username,
          email: plan.finalEmail,
          password: user.username.replace('-', '').slice(0, -1),
          first_name: user.first_name,
          last_name: user.last_name,
          role: 'customer'
        });
      }

      // 2. PREPARAR DATOS PARA WORDPRESS
      const userData = {
        username: user.username,
        email: user.email,
        password: user.username.replace('-', '').slice(0, -1),
        first_name: user.first_name,
        last_name: user.last_name,
        billing: user.billing,
        meta_data: user.meta_data
      };

      // Usuarios nuevos: siempre customer. Existentes: mantiene rol actual.
      if (!existing) {
        userData.role = 'customer';
      }

      // Existente y el archivo no trae email: no pisar el email real con el generado por RUT
      if (existing && !user._emailFromFile) {
        delete userData.email;
        const { email: _omit, ...billingSinEmail } = user.billing || {};
        userData.billing = billingSinEmail;
        userData.meta_data = user.meta_data.filter(m => m.key !== 'apoderado_email');
      }

      // 3. REGISTRO EN WORDPRESS (siempre)
      const customerResult = existing
        ? await updateCustomer(existing.id, { ...userData, username: undefined })
        : await createCustomer(userData);

      // 4. VALIDACIÓN DUAL (igual que modal)
      if (!(registrationResult.success && customerResult.success)) {
        const errorMessage = [
          registrationResult.message, 
          customerResult.message
        ].filter(Boolean).join('. ');

        return { 
          username: user.username, 
          success: false, 
          action: 'error', 
          message: errorMessage,
          expectedEmail: plan.finalEmail,
          expectedGroup: plan.finalGroup
        };
      }

      // 5. VERIFICACIÓN: releer el usuario y comparar con lo esperado
      const after = await getCustomerByUsername(user.username);
      const actualEmail = after ? (after.email || '') : '';
      const actualGroup = after ? getMeta(after, 'group_name') : '';
      const issues = [];
      if (!after) {
        issues.push('No se pudo releer el usuario para verificar');
      } else {
        if (plan.finalEmail && actualEmail.toLowerCase() !== plan.finalEmail.toLowerCase()) {
          issues.push(`Email esperado ${plan.finalEmail}, quedó ${actualEmail || '(vacío)'}`);
        }
        if (plan.finalGroup && actualGroup !== plan.finalGroup) {
          issues.push(`Curso esperado "${plan.finalGroup}", quedó "${actualGroup || '(vacío)'}"`);
        }
      }

      return {
        username: user.username,
        success: issues.length === 0,
        action: issues.length ? 'error' : (existing ? 'updated' : 'created'),
        laravelRegistered: moduleStatus[MODULES.SURVEYS],
        message: issues.length ? issues.join('. ') : undefined,
        expectedEmail: plan.finalEmail,
        expectedGroup: plan.finalGroup,
        actualEmail,
        actualGroup,
        emailNote: plan.emailNote,
        groupNote: plan.groupNote
      };
    } catch (error) {
      return { 
        username: user.username, 
        success: false, 
        action: 'error', 
        message: error.message 
      };
    }
  };

  // Iniciar procesamiento
  const startProcessing = async () => {
    if (!operationName.trim()) {
      toast({
        title: "Error",
        description: "Ingresa un nombre para la operación",
        status: "error",
        duration: 3000
      });
      return;
    }

    if (!processedUsers.length) {
      toast({
        title: "Error", 
        description: "No hay usuarios para procesar",
        status: "error",
        duration: 3000
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setResults([]);
    
    const newStats = { total: 0, success: 0, errors: 0, created: 0, updated: 0, laravelRegistered: 0 };

    try {
      const tasks = [...processedUsers];
      newStats.total = tasks.length;

      // Procesar en lotes
      const BATCH_SIZE = 10;
      for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
        const batch = tasks.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (item) => {
          const result = await processUser(item);

          if (result.success) {
            newStats.success++;
            if (result.action === 'created') newStats.created++;
            if (result.action === 'updated') newStats.updated++;
            if (result.laravelRegistered) newStats.laravelRegistered++;
          } else {
            newStats.errors++;
          }

          setStats({ ...newStats });
          setProgress(((newStats.success + newStats.errors) / newStats.total) * 100);
          
          return result;
        });

        const batchResults = await Promise.allSettled(batchPromises);
        const successResults = batchResults
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);
        
        setResults(prev => [...prev, ...successResults]);

        if (i + BATCH_SIZE < tasks.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      toast({
        title: "Proceso completado",
        description: `Total: ${newStats.total}, Exitosos: ${newStats.success}, Errores: ${newStats.errors}`,
        status: newStats.errors === 0 ? "success" : "warning",
        duration: 5000
      });

    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Return JSX completo
  return (
    <Container maxW="7xl" py={4}>
      <VStack spacing={6} align="stretch">
        {/* Configuración inicial */}
        <Card maxW="800px" mx="auto">
          <CardHeader>
            <Heading size="md">Configuración</Heading>
          </CardHeader>
          <CardBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Nombre de la operación</FormLabel>
                <Input
                  value={operationName}
                  onChange={(e) => setOperationName(e.target.value)}
                  placeholder="Ej: Importación Enero 2025"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Archivo Excel/CSV</FormLabel>
                <Input
                  type="file"
                  accept=".xls,.xlsx,.csv"
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Soporta Excel y CSV con datos de alumnos y apoderados
                </Text>
              </FormControl>
            </Stack>
          </CardBody>
        </Card>

        {/* Mapeo de columnas */}
        {showMappingUI && (
          <Card maxW="100%" mx="auto">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <div>
                  <Heading size="md">Mapeo de Columnas</Heading>
                  <Text fontSize="sm" color="gray.500">
                    Ajusta qué columna corresponde a cada campo
                  </Text>
                </div>
                <Button size="sm" onClick={() => autoMapColumns(fileHeaders)}>
                  Auto-mapear
                </Button>
              </Flex>
            </CardHeader>
            <CardBody>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                {/* Datos del Alumno */}
                <GridItem>
                  <Box p={3} bg="blue.50" borderRadius="md" borderWidth={1} borderColor="blue.200">
                    <Heading size="sm" mb={3} color="blue.700">Datos del Alumno</Heading>
                    <Stack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">RUN/RUT *</FormLabel>
                        <Select value={columnMap.studentRun} onChange={(e) => updateMapping('studentRun', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Nombre *</FormLabel>
                        <Select value={columnMap.studentName} onChange={(e) => updateMapping('studentName', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Apellido Paterno</FormLabel>
                        <Select value={columnMap.studentLastNameP} onChange={(e) => updateMapping('studentLastNameP', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Apellido Materno</FormLabel>
                        <Select value={columnMap.studentLastNameM} onChange={(e) => updateMapping('studentLastNameM', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Email (fallback si no hay financiero)</FormLabel>
                        <Select value={columnMap.studentEmail} onChange={(e) => updateMapping('studentEmail', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Dirección</FormLabel>
                        <Select value={columnMap.studentAddress} onChange={(e) => updateMapping('studentAddress', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Comuna</FormLabel>
                        <Select value={columnMap.studentCommune} onChange={(e) => updateMapping('studentCommune', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Tipo Enseñanza</FormLabel>
                        <Select value={columnMap.educationType} onChange={(e) => updateMapping('educationType', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Grado</FormLabel>
                        <Select value={columnMap.gradeName} onChange={(e) => updateMapping('gradeName', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Curso</FormLabel>
                        <Select value={columnMap.courseName} onChange={(e) => updateMapping('courseName', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                </GridItem>

                {/* Apoderado Financiero */}
                <GridItem>
                  <Box p={3} bg="green.50" borderRadius="md" borderWidth={1} borderColor="green.200">
                    <Heading size="sm" mb={3} color="green.700">Apoderado Financiero (Prioridad)</Heading>
                    <Stack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">RUN Financiero</FormLabel>
                        <Select value={columnMap.finRun} onChange={(e) => updateMapping('finRun', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Nombre Financiero</FormLabel>
                        <Select value={columnMap.finName} onChange={(e) => updateMapping('finName', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Apellido Paterno Financiero</FormLabel>
                        <Select value={columnMap.finLastNameP} onChange={(e) => updateMapping('finLastNameP', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Apellido Materno Financiero</FormLabel>
                        <Select value={columnMap.finLastNameM} onChange={(e) => updateMapping('finLastNameM', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Email Financiero</FormLabel>
                        <Select value={columnMap.finEmail} onChange={(e) => updateMapping('finEmail', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Teléfono Financiero</FormLabel>
                        <Select value={columnMap.finPhone} onChange={(e) => updateMapping('finPhone', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Dirección Apoderado</FormLabel>
                        <Select value={columnMap.finAddress} onChange={(e) => updateMapping('finAddress', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Comuna Apoderado</FormLabel>
                        <Select value={columnMap.finCommune} onChange={(e) => updateMapping('finCommune', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                </GridItem>

                {/* Apoderado Académico */}
                <GridItem>
                  <Box p={3} bg="purple.50" borderRadius="md" borderWidth={1} borderColor="purple.200">
                    <Heading size="sm" mb={3} color="purple.700">Apoderado Académico (Fallback)</Heading>
                    <Stack spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">RUN Académico</FormLabel>
                        <Select value={columnMap.acadRun} onChange={(e) => updateMapping('acadRun', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Nombre Académico</FormLabel>
                        <Select value={columnMap.acadName} onChange={(e) => updateMapping('acadName', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Apellido Paterno Académico</FormLabel>
                        <Select value={columnMap.acadLastNameP} onChange={(e) => updateMapping('acadLastNameP', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Apellido Materno Académico</FormLabel>
                        <Select value={columnMap.acadLastNameM} onChange={(e) => updateMapping('acadLastNameM', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Teléfono Académico</FormLabel>
                        <Select value={columnMap.acadPhone} onChange={(e) => updateMapping('acadPhone', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Dirección Apoderado (Acad.)</FormLabel>
                        <Select value={columnMap.acadAddress} onChange={(e) => updateMapping('acadAddress', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Comuna Apoderado (Acad.)</FormLabel>
                        <Select value={columnMap.acadCommune} onChange={(e) => updateMapping('acadCommune', e.target.value)}>
                          <option value="">Seleccionar...</option>
                          {fileHeaders.map((h, i) => <option key={i} value={h}>{h}</option>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                </GridItem>
              </Grid>

            </CardBody>
              <Alert status="info" mt={4}>
                <AlertIcon />
                <Box>
                  <Text fontWeight="medium">Lógica de Apoderado:</Text>
                  <Text fontSize="sm">
                    • Se prioritiza el <strong>Apoderado Financiero</strong>
                  </Text>
                  <Text fontSize="sm">
                    • Si no hay datos del financiero, se usa el <strong>Apoderado Académico</strong>
                  </Text>
                  <Text fontSize="sm">
                    • Los campos con * son obligatorios
                  </Text>
                </Box>
              </Alert>
          </Card>
        )}

        {/* Vista previa y gestión de roles */}
        {processedUsers.length > 0 && (
          <Card>
            <CardBody>
              <Stack spacing={4}>
                {/* Header vista previa */}
                <Flex justify="space-between" align="center">
                  <div>
                    <Heading size="md">Vista Previa</Heading>
                    <Text color="gray.500" fontSize="sm">
                      Archivo: {fileName} • {processedUsers.length} registros
                    </Text>
                  </div>
                  <VStack align="end" spacing={1}>
                    <HStack>
                      <Badge colorScheme="blue">Sistema CRM</Badge>
                      {moduleStatus[MODULES.SURVEYS] && (
                        <Badge colorScheme="green">+ Encuestas/Casos</Badge>
                      )}
                    </HStack>
                  </VStack>
                </Flex>

                {/* Tabla vista previa */}
                <Box overflowX="auto" maxH="400px" overflowY="auto">
                  <Table size="sm">
                    <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                      <Tr>
                        <Th>RUN Alumno</Th>
                        <Th>Nombre</Th>
                        <Th>Apellidos</Th>
                        <Th>Email actual → final</Th>
                        <Th>Curso actual → final</Th>
                        <Th>Dirección</Th>
                        <Th>Comuna</Th>
                        <Th>Tipo Enseñanza</Th>
                        <Th>Apoderado RUT</Th>
                        <Th>Apoderado Nombre</Th>
                        <Th>Apoderado Email</Th>
                        <Th>Apoderado Teléfono</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {processedUsers.slice(0, 10).map((user, index) => {
                        const plan = buildPlan(user, existingMap[user.username]);
                        const noteColor = (n) => !n ? 'gray.500' : n.startsWith('Cambia') || n.startsWith('Nuevo') ? 'orange.500' : 'gray.500';
                        return (
                        <Tr key={index}>
                          <Td fontWeight="medium">{user.username}</Td>
                          <Td>{user.first_name}</Td>
                          <Td>{user.last_name}</Td>
                          <Td fontSize="sm">
                            {plan.pending ? (
                              <Text color="gray.500">{isLookingUp ? 'Consultando...' : 'No se pudo consultar'} (archivo: {user.email})</Text>
                            ) : (
                              <>
                                {plan.exists && <Text color="gray.500">{plan.currentEmail || '(vacío)'} →</Text>}
                                <Text fontWeight="medium">{plan.finalEmail || '-'}</Text>
                                <Text fontSize="xs" color={noteColor(plan.emailNote)}>{plan.emailNote}</Text>
                              </>
                            )}
                          </Td>
                          <Td fontSize="sm">
                            {plan.pending ? (
                              <Text color="gray.500">{plan.finalGroup || '-'}</Text>
                            ) : (
                              <>
                                {plan.exists && <Text color="gray.500">{plan.currentGroup || '(vacío)'} →</Text>}
                                <Text fontWeight="medium">{plan.finalGroup || '-'}</Text>
                                <Text fontSize="xs" color={noteColor(plan.groupNote)}>{plan.groupNote}</Text>
                              </>
                            )}
                          </Td>
                          <Td fontSize="sm">{user._address || '-'}</Td>
                          <Td fontSize="sm">{user._commune || '-'}</Td>
                          <Td fontSize="sm">
                            <Badge colorScheme="purple" variant="subtle">
                              {user._educationType || '-'}
                            </Badge>
                          </Td>
                          <Td fontSize="sm" fontWeight="bold" color="green.600">
                            {user._apoderadoRut || '-'}
                          </Td>
                          <Td fontSize="sm" color="green.600">
                            {user._apoderadoNombre || '-'}
                          </Td>
                          <Td fontSize="sm" color="green.600">
                            {user._apoderadoEmail || '-'}
                          </Td>
                          <Td fontSize="sm" color="green.600">
                            {user._apoderadoTelefono || '-'}
                          </Td>
                        </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>

                {processedUsers.length > 10 && (
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Mostrando 10 de {processedUsers.length} registros
                  </Text>
                )}
              </Stack>
            </CardBody>
          </Card>
        )}

        {/* Botón procesar */}
        <Button
          colorScheme="teal"
          size="lg"
          onClick={startProcessing}
          isLoading={isProcessing}
          loadingText="Procesando..."
          isDisabled={!operationName.trim() || !processedUsers.length}
        >
          {isProcessing 
            ? `Procesando... (${Math.round(progress)}%)`
            : `Procesar ${processedUsers.length} usuarios`
          }
        </Button>

        {/* Progreso */}
        {isProcessing && (
          <Box>
            <Progress value={progress} colorScheme="teal" size="lg" borderRadius="md" />
            <HStack justify="space-between" mt={2}>
              <Text fontSize="sm">
                Procesados: {stats.success + stats.errors} / {stats.total}
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {Math.round(progress)}%
              </Text>
            </HStack>
          </Box>
        )}

        {/* Estadísticas */}
        {stats.total > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Estadísticas</Heading>
            </CardHeader>
            <CardBody>
              <HStack wrap="wrap" spacing={4}>
                <Badge colorScheme="blue" variant="solid" p={2} borderRadius="md">
                  Total: {stats.total}
                </Badge>
                <Badge colorScheme="green" variant="solid" p={2} borderRadius="md">
                  Exitosos: {stats.success}
                </Badge>
                <Badge colorScheme="red" variant="solid" p={2} borderRadius="md">
                  Errores: {stats.errors}
                </Badge>
                {stats.created > 0 && (
                  <Badge colorScheme="purple" variant="solid" p={2} borderRadius="md">
                    Creados: {stats.created}
                  </Badge>
                )}
                {stats.updated > 0 && (
                  <Badge colorScheme="orange" variant="solid" p={2} borderRadius="md">
                    Actualizados: {stats.updated}
                  </Badge>
                )}
                {stats.laravelRegistered > 0 && (
                  <Badge colorScheme="green" variant="solid" p={2} borderRadius="md">
                    Con Acceso Completo: {stats.laravelRegistered}
                  </Badge>
                )}
              </HStack>
            </CardBody>
          </Card>
        )}

        {/* Resultados detallados */}
        {results.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Resultados Detallados</Heading>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto" maxH="500px" overflowY="auto">
                <Table size="sm">
                  <Thead position="sticky" top={0} bg={bgColor} zIndex={1}>
                    <Tr>
                      <Th>Usuario</Th>
                      <Th>Acción</Th>
                      <Th>Sistemas</Th>
                      <Th>Estado</Th>
                      <Th>Email final (verificado)</Th>
                      <Th>Curso final (verificado)</Th>
                      <Th>Mensaje</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {results.map((result, index) => (
                      <Tr key={index}>
                        <Td fontWeight="medium">{result.username}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Badge
                              colorScheme={
                                result.action === 'created' ? 'purple' :
                                result.action === 'updated' ? 'orange' : 'gray'
                              }
                              variant="solid"
                            >
                              {result.action === 'created' ? 'Creado' :
                               result.action === 'updated' ? 'Actualizado' : 'Error'}
                            </Badge>
                          </HStack>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Badge colorScheme="blue" variant="solid" size="sm">
                              CRM
                            </Badge>
                            {result.laravelRegistered && (
                              <Badge colorScheme="green" variant="solid" size="sm">
                                Completo
                              </Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td>
                          <Badge 
                            colorScheme={result.success ? 'green' : 'red'}
                            variant="solid"
                          >
                            {result.success ? 'Éxito' : 'Error'}
                          </Badge>
                        </Td>
                        <Td fontSize="sm">{result.actualEmail || '-'}</Td>
                        <Td fontSize="sm">{result.actualGroup || '-'}</Td>
                        <Td fontSize="sm">{result.message || 'Verificado: coincide con lo esperado'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        )}

        {/* Información - VERSIÓN DUAL AUTOMÁTICA */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="medium">
              Importador Dual Automático - Características:
            </Text>
            <Text fontSize="sm">
              • <strong>Acceso completo automático</strong>: Usuarios con acceso a todas las funcionalidades del sistema
            </Text>
            <Text fontSize="sm">
              • <strong>Modo conservador</strong>: No cambia roles de usuarios existentes por defecto
            </Text>
            <Text fontSize="sm">
              • <strong>Usuarios nuevos</strong>: Siempre se crean como 'customer' con acceso completo
            </Text>
            <Text fontSize="sm">
              • <strong>Usuarios existentes</strong>: Solo actualiza datos, mantiene rol actual (usa la pestana "Sincronizar Roles" para cambiar roles)
            </Text>
            <Text fontSize="sm" color={moduleStatus[MODULES.SURVEYS] ? "green.600" : "orange.500"}>
              • {moduleStatus[MODULES.SURVEYS] ? '✅' : '⚠️'} Funcionalidades avanzadas {moduleStatus[MODULES.SURVEYS] ? 'ACTIVAS' : 'LIMITADAS'}
              {moduleStatus[MODULES.SURVEYS] && ' - Encuestas, casos y gestión de archivos disponibles'}
            </Text>
            <Text fontSize="sm">
              • <strong>Mapeo inteligente</strong>: Detecta columnas automáticamente
            </Text>
            <Text fontSize="sm">
              • <strong>Lógica apoderado</strong>: Financiero prioritario, académico como fallback
            </Text>
          </VStack>
        </Alert>
      </VStack>
    </Container>
  );
};

export default UnifiedUserImporter;